// src/pages/Profile.jsx
// Members, account page. Protected by ProtectedRoute, so it only renders for a
// logged-in user. Shows the account details from the auth context and holds the
// logout button. Admins also get a shortcut into the dashboard.

import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";

export default function Profile() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  // Defensive: ProtectedRoute guarantees a user, but guard against a direct render.
  if (!user) return null;

  function handleLogout() {
    logout();
    navigate("/");
  }

  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const roleLabel = user.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
    : "User";

  return (
    <>
      {/* Page header */}
      <section className="max-w-wide mx-auto px-6 pt-20 pb-12 md:pt-28 md:pb-16">
        <p className="label-mono">Members</p>
        <h1 className="font-display text-h2 leading-tight font-medium mt-3">
          {user.name}
        </h1>
        <p className="text-body text-ink-70 leading-relaxed mt-6 max-w-2xl">
          Your account on the masthead.
        </p>
      </section>

      <hr className="hairline max-w-wide mx-auto" />

      {/* Account details + actions */}
      <section className="max-w-wide mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Details */}
          <div className="md:col-span-8">
            <p className="label-mono mb-8">Account</p>
            <ul className="space-y-6">
              <li>
                <p className="label-mono mb-1">Username</p>
                <p className="text-body text-ink">{user.username}</p>
              </li>
              <li>
                <p className="label-mono mb-1">Email</p>
                <p className="text-body text-ink">{user.email}</p>
              </li>
              <li>
                <p className="label-mono mb-1">Role</p>
                <p className="text-body text-ink">{roleLabel}</p>
              </li>
              {memberSince && (
                <li>
                  <p className="label-mono mb-1">Member since</p>
                  <p className="text-body text-ink">{memberSince}</p>
                </li>
              )}
            </ul>
          </div>

          {/* Actions */}
          <div className="md:col-span-4">
            <p className="label-mono mb-8">Actions</p>
            <div className="space-y-6">
              {isAdmin && (
                <div>
                  <Link
                    to="/admin-dash"
                    className="label-mono border-b border-ink hover:text-oxblood hover:border-oxblood transition-colors py-2"
                  >
                    Open the dashboard →
                  </Link>
                </div>
              )}
              <div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="label-mono border-b border-ink hover:text-oxblood hover:border-oxblood transition-colors py-2"
                >
                  Sign out →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
